import { Link, useLocation } from "react-router-dom";
import { Menu, X, Home, Building2, HelpCircle, Tag, UserPlus, Mail, LogIn, User, ChevronDown, Info, Calculator, Award, BookOpen, FileText, PhoneCall, Shield, DollarSign, Users, Briefcase } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { resolveImageUrl } from "@/utils/imageUrl";
import { useNavigation } from "@/hooks/useNavigation";
import { getApiUrl } from "@/utils/api";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_realty-gallery/artifacts/zzc7uqc2_Screenshot_20260119_035617_Chrome.ico";
const API = getApiUrl();

// Extended icon mapping for navigation items
const NAV_ICON_MAP = {
  // Main pages
  "/": Home,
  "/properties": Building2,
  "/property": Building2,
  "/how-it-works": HelpCircle,
  "/about": Info,
  "/contact": Mail,
  
  // Services & Features
  "/valuation": Calculator,
  "/testimonials": Award,
  "/guides": BookOpen,
  "/calculators": Calculator,
  "/selling-guides": FileText,
  "/buying-guides": FileText,
  
  // Business
  "/prices": DollarSign,
  "/pricing": DollarSign,
  "/join-us": UserPlus,
  "/agents": Users,
  "/careers": Briefcase,
  
  // Contact & Support
  "/support": PhoneCall,
  "/help": HelpCircle,
  "/faq": HelpCircle,
  "/terms": Shield,
  "/privacy": Shield,
  
  // Default
  "/property-admin/login": LogIn,
};

const getIconForPath = (path) => {
  // Try exact match first
  if (NAV_ICON_MAP[path]) return NAV_ICON_MAP[path];
  
  // Try partial match (for dynamic routes)
  const pathKey = Object.keys(NAV_ICON_MAP).find(key => 
    path.startsWith(key) && key !== "/"
  );
  
  return pathKey ? NAV_ICON_MAP[pathKey] : Building2; // Default to Building2 for unknown paths
};

export const Navbar = ({ variant = "default" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const location = useLocation();
  const { styles } = useSiteStyles();
  const { navigation, loading } = useNavigation();
  
  // Check for active SSO session
  useEffect(() => {
    const checkSession = async () => {
      try {
        await axios.get(`${API}/property-admin/me`, { withCredentials: true });
        setIsLoggedIn(true);
      } catch {
        setIsLoggedIn(false);
      } finally {
        setCheckingSession(false);
      }
    };
    
    checkSession();
    
    // Re-check session when location changes (e.g., after login/logout)
    const interval = setInterval(checkSession, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [location.pathname]);
  
  // Ensure styles are applied after mount
  useEffect(() => {
    setMounted(true);
  }, []);
  
  const isTransparent = variant === "transparent";
  
  // Get navigation settings from styles with fallbacks
  const navBgColor = isTransparent ? "transparent" : (styles?.navBgHex || "#ffffff");
  const navTextColor = isTransparent ? "#ffffff" : (styles?.navTextHex || "#1a1a1a");
  const navHoverColor = styles?.navHoverHex || "#2f4f4f";
  const navButtonBg = isTransparent ? "#ffffff" : (styles?.navButtonBgHex || "#2f4f4f");
  const navButtonText = isTransparent ? (styles?.navButtonBgHex || "#2f4f4f") : (styles?.navButtonTextHex || "#ffffff");
  const logoText = styles?.navLogoText || "SellLocalHomes";
  const ctaText = styles?.navCtaText || "Request Viewing";
  const ctaEnabled = styles?.navCtaEnabled !== false;
  const navFontSize = styles?.navFontSize || 14;
  const navLogoFontSize = styles?.navLogoFontSize || 20;
  
  // Toggle mobile dropdown
  const toggleMobileDropdown = (navId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [navId]: !prev[navId]
    }));
  };

  // Build inline styles object
  const navStyle = isTransparent ? {} : { backgroundColor: navBgColor };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 ${isTransparent ? "bg-transparent" : "border-b border-border/50"}`} 
      style={navStyle}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Top Row: Logo and Logo Text */}
        <div className="flex items-center justify-between pt-4 pb-2">
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img src={styles.companyLogo ? resolveImageUrl(styles.companyLogo) : DEFAULT_LOGO} alt={logoText} className="h-12 w-12 object-contain" />
            <span 
              className="font-serif font-medium"
              style={{ color: navTextColor, fontSize: `${navLogoFontSize}px` }}
            >
              {logoText}
            </span>
          </Link>
          
          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            style={{ color: navTextColor }}
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {/* Bottom Row: Navigation Links */}
        <div className="hidden md:flex items-center justify-center gap-8 pb-4">
          {!loading && navigation.map((item) => {
            const isActive = location.pathname === item.path;
            const hasChildren = item.children && item.children.length > 0;
            const ItemIcon = getIconForPath(item.path);
            
            // If item has children, render dropdown menu — label navigates
            // to the parent's own page, chevron opens the children dropdown
            if (hasChildren) {
              return (
                <div key={item.nav_id} className="flex items-center group">
                  <Link
                    to={item.path}
                    className="nav-link font-medium tracking-wide transition-all flex items-center gap-1.5 outline-none"
                    style={{
                      color: navTextColor,
                      opacity: 0.75,
                      fontSize: `${navFontSize}px`
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = navHoverColor; e.currentTarget.style.opacity = 1; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = navTextColor; e.currentTarget.style.opacity = 0.75; }}
                  >
                    <ItemIcon size={Math.max(14, navFontSize - 2)} className="shrink-0" />
                    <span>{item.title}</span>
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="outline-none p-1"
                        style={{ color: navTextColor, opacity: 0.75 }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.75; }}
                        data-testid={`nav-dropdown-trigger-${item.nav_id}`}
                      >
                        <ChevronDown size={14} className="transition-transform group-data-[state=open]:rotate-180" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      {item.children.map(child => {
                        const ChildIcon = getIconForPath(child.path);
                        return (
                          <DropdownMenuItem key={child.nav_id} asChild>
                            <Link
                              to={child.path}
                              className="cursor-pointer w-full flex items-center gap-2"
                            >
                              <ChildIcon size={16} className="shrink-0 text-muted-foreground" />
                              <span>{child.title}</span>
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            }
            
            // Regular link (no children)
            return (
              <Link
                key={item.nav_id}
                to={item.path}
                className="nav-link font-medium tracking-wide transition-all flex items-center gap-1.5 group"
                style={{
                  color: isActive ? navHoverColor : navTextColor,
                  opacity: isActive ? 1 : 0.75,
                  fontSize: `${navFontSize}px`
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = navHoverColor; e.currentTarget.style.opacity = 1; }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = isActive ? navHoverColor : navTextColor;
                  e.currentTarget.style.opacity = isActive ? 1 : 0.75;
                }}
              >
                <ItemIcon size={Math.max(14, navFontSize - 2)} className="shrink-0 transition-transform group-hover:scale-110" />
                <span>{item.title}</span>
              </Link>
            );
          })}
          
          {/* Show My Account if logged in, otherwise show Login */}
          {!checkingSession && (
            isLoggedIn ? (
              <Link
                to="/property-admin/dashboard"
                className="nav-link font-medium tracking-wide transition-all flex items-center gap-1.5 group"
                style={{
                  color: location.pathname === "/property-admin/dashboard" ? navHoverColor : navTextColor,
                  opacity: location.pathname === "/property-admin/dashboard" ? 1 : 0.75,
                  fontSize: `${navFontSize}px`
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = navHoverColor; e.currentTarget.style.opacity = 1; }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = location.pathname === "/property-admin/dashboard" ? navHoverColor : navTextColor;
                  e.currentTarget.style.opacity = location.pathname === "/property-admin/dashboard" ? 1 : 0.75;
                }}
                data-testid="nav-link-my-account"
              >
                <User size={Math.max(14, navFontSize - 2)} className="shrink-0 transition-transform group-hover:scale-110" />
                <span>My Account</span>
              </Link>
            ) : (
              <Link
                to="/property-admin/login"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link font-medium tracking-wide transition-all flex items-center gap-1.5 group"
                style={{
                  color: location.pathname === "/property-admin/login" ? navHoverColor : navTextColor,
                  opacity: location.pathname === "/property-admin/login" ? 1 : 0.75,
                  fontSize: `${navFontSize}px`
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = navHoverColor; e.currentTarget.style.opacity = 1; }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = location.pathname === "/property-admin/login" ? navHoverColor : navTextColor;
                  e.currentTarget.style.opacity = location.pathname === "/property-admin/login" ? 1 : 0.75;
                }}
                data-testid="nav-link-login"
              >
                <LogIn size={Math.max(14, navFontSize - 2)} className="shrink-0 transition-transform group-hover:scale-110" />
                <span>Login</span>
              </Link>
            )
          )}
          
          {ctaEnabled && (
            <Link to="/contact">
              <Button
                className="btn-lift px-6 py-2 font-medium rounded-none"
                style={{ backgroundColor: navButtonBg, color: navButtonText, fontSize: `${navFontSize}px` }}
                data-testid="nav-enquire-btn"
              >
                {ctaText}
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="md:hidden border-t border-border/30 animate-fade-in"
          >
            <div className="flex flex-col py-4 gap-3">
              {!loading && navigation.map((item) => {
                const isActive = location.pathname === item.path;
                const hasChildren = item.children && item.children.length > 0;
                const ItemIcon = getIconForPath(item.path);
                const isDropdownOpen = openDropdowns[item.nav_id];
                
                if (hasChildren) {
                  return (
                    <div key={item.nav_id}>
                      <div className="flex items-center justify-between w-full">
                        <Link
                          to={item.path}
                          className="text-base font-medium py-2 flex items-center gap-2 flex-1"
                          style={{ color: navTextColor, opacity: 0.7 }}
                          onClick={() => setIsOpen(false)}
                        >
                          <ItemIcon size={18} className="shrink-0" />
                          <span>{item.title}</span>
                        </Link>
                        <button
                          className="py-2 px-2"
                          style={{ color: navTextColor, opacity: 0.7 }}
                          onClick={() => toggleMobileDropdown(item.nav_id)}
                          data-testid={`nav-mobile-dropdown-trigger-${item.nav_id}`}
                        >
                          <ChevronDown
                            size={16}
                            className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                      </div>
                      {isDropdownOpen && (
                        <div className="pl-4 mt-2 space-y-2">
                          {item.children.map(child => {
                            const ChildIcon = getIconForPath(child.path);
                            return (
                              <Link
                                key={child.nav_id}
                                to={child.path}
                                className="text-sm font-medium py-2 flex items-center gap-2"
                                style={{
                                  color: navTextColor,
                                  opacity: location.pathname === child.path ? 1 : 0.6
                                }}
                                onClick={() => setIsOpen(false)}
                              >
                                <ChildIcon size={16} className="shrink-0" />
                                {child.title}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.nav_id}
                    to={item.path}
                    className="text-base font-medium py-2 flex items-center gap-2"
                    style={{
                      color: navTextColor,
                      opacity: isActive ? 1 : 0.7
                    }}
                    onClick={() => setIsOpen(false)}
                  >
                    <ItemIcon size={18} className="shrink-0" />
                    <span>{item.title}</span>
                  </Link>
                );
              })}
              
              {/* Mobile: Show My Account if logged in, otherwise show Login */}
              {!checkingSession && (
                isLoggedIn ? (
                  <Link
                    to="/property-admin/dashboard"
                    className="text-base font-medium py-2 flex items-center gap-2"
                    style={{
                      color: navTextColor,
                      opacity: location.pathname === "/property-admin/dashboard" ? 1 : 0.7
                    }}
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-nav-link-my-account"
                  >
                    <User size={16} className="shrink-0" />
                    <span>My Account</span>
                  </Link>
                ) : (
                  <Link
                    to="/property-admin/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base font-medium py-2 flex items-center gap-2"
                    style={{
                      color: navTextColor,
                      opacity: location.pathname === "/property-admin/login" ? 1 : 0.7
                    }}
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-nav-link-login"
                  >
                    <LogIn size={16} className="shrink-0" />
                    <span>Login</span>
                  </Link>
                )
              )}
              {ctaEnabled && (
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Button 
                    className="w-full mt-2 rounded-none" 
                    style={{ backgroundColor: navButtonBg, color: navButtonText }}
                    data-testid="mobile-enquire-btn"
                  >
                    {ctaText}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
